import { router, usePage } from "@inertiajs/vue3";
import mitt from "mitt";
import { computed, ref } from "vue";

import { getCookie } from "../utils";
import { initializeAmplitudeEngagement, trackPageView } from "./utils/amplitude";

export default {
    /*
     * `install` only runs once, and is passed the props of the page that is
     * first rendered at page load. These props are used as the initial value
     * for some values that are immutable (events url, etc).
     */
    install: (app, initialProps) => {
        const { event_url, feedback_mailto, start_chat_url, loading_image_src, user_details, is_debug, public_runtime_config } = initialProps;

        const eventContext = ref({});

        const sendEvent = async function (eventType, data) {
            const response = await fetch(event_url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRFToken": getCookie("csrftoken"),
                },
                body: JSON.stringify({
                    event_type: eventType,
                    ...data,
                }),
            });

            const body = await response.json();
            body.status = response.status;

            if (!response.ok) {
                return Promise.reject(body);
            }

            return body;
        };

        const sendSocketEvent = function (eventType, data, socket, retries = 3, delay = 500) {
            // Sends event over the socket, with retry handling
            // Handles both synchronous (WebSocket) and async (SSE) send() methods
            const message = JSON.stringify({
                event_type: eventType,
                ...data,
                request_id: crypto.randomUUID(),
            });

            const attemptSend = async (attempt = 0) => {
                try {
                    const result = socket.send(message);

                    // If send() returns a Promise (SSE), await it and handle errors
                    if (result instanceof Promise) {
                        try {
                            await result;
                        } catch (error) {
                            // Promise rejection - handle retry logic
                            if (attempt < retries) {
                                const backoffDelay = delay * Math.pow(2, attempt);
                                await new Promise((resolve) => setTimeout(resolve, backoffDelay));
                                return attemptSend(attempt + 1);
                            } else {
                                throw new Error(`Failed to send socket message after ${retries} retries: ${error.message}`);
                            }
                        }
                    }
                    // If send() is synchronous (WebSocket), any errors are caught by outer try/catch
                } catch (error) {
                    // Synchronous error (WebSocket) - handle retry logic
                    if (attempt < retries) {
                        const backoffDelay = delay * Math.pow(2, attempt);
                        setTimeout(() => attemptSend(attempt + 1), backoffDelay);
                    } else {
                        // After all retries fail, throw the error
                        throw new Error(`Failed to send socket message after ${retries} retries: ${error.message}`);
                    }
                }
            };

            attemptSend().catch((error) => {
                // Handle any unhandled promise rejections
                console.error("Unhandled error in sendSocketEvent:", error);
            });
        };

        const emitter = mitt();

        // Set up emitters
        app.config.globalProperties.emitter = emitter;

        // Set up global constants with server side variables.
        app.config.globalProperties.$loadingImageSrc = loading_image_src;
        app.config.globalProperties.$startChatUrl = start_chat_url;
        app.config.globalProperties.$feedbackMailto = feedback_mailto;

        // Helper methods for sending events.
        // Attach any properties that will be sent in every $sendEvent call for a page
        app.config.globalProperties.$setEventContext = (context) => {
            eventContext.value = context;
        };
        app.config.globalProperties.$sendEvent = async (event_type, data) => {
            return sendEvent(event_type, { ...eventContext.value, ...data });
        };
        app.config.globalProperties.isDebug = is_debug;

        // initializeAmplitudeEngagement also enforces cookie consent ("all" required) — see BUG-625.
        if (user_details) {
            initializeAmplitudeEngagement(user_details, public_runtime_config?.amplitudeApiKey);
            // Inertia swaps pages without a document load, and fires `navigate` for
            // the first page as well as every later one — so this single listener
            // covers the whole session. Registered before the Inertia plugin
            // installs, so the initial page is never missed.
            router.on("navigate", (event) => {
                trackPageView({ client: event.detail.page.props.client, path: globalThis.location.pathname });
            });
        }

        const page = usePage();
        app.provide(
            "user_details",
            computed(() => page.props.user_details),
        );
        app.provide(
            "organization_details",
            computed(() => page.props.organization_details),
        );
        app.provide(
            "align_host",
            computed(() => page.props.align_host),
        );

        app.config.globalProperties.$sendSocketEvent = (event_type, data, socket) => {
            return sendSocketEvent(event_type, { ...eventContext.value, ...data }, socket);
        };

        // Make $sendEvent available to composition API components, as in: `const sendEvent = inject("sendEvent")`
        app.provide("sendEvent", app.config.globalProperties.$sendEvent);
        app.provide("globalProperties", app.config.globalProperties);
    },
};
